import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { Card, CardBody, CardFooter, CardHeader } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Checkbox } from '../components/Field.js'
import { PhoneField } from '../components/PhoneField.js'
import { StatusPill } from '../components/StatusPill.js'
import { FeatureGate } from '../components/FeatureGate.js'
import { FEATURES } from '@academy/config'

export const Route = createFileRoute('/_app/teachers')({
  component: () => (
    <FeatureGate feature={FEATURES.TEACHER_ACCOUNTS}>
      <TeachersPage />
    </FeatureGate>
  ),
})

interface TeacherRow {
  userId: string
  email: string
  fullName: string | null
  phone: string | null
  isActive: boolean
  classes: { id: string; name: string }[]
}

interface ClassRow {
  id: string
  name: string
  isActive: boolean
}

function TeachersPage() {
  const qc = useQueryClient()

  const teachers = useQuery({
    queryKey: ['teachers'],
    queryFn: async () => {
      const res = await api<TeacherRow[]>('/api/v1/teachers')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [classIds, setClassIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await api('/api/v1/teachers', {
        method: 'POST',
        body: JSON.stringify({
          fullName,
          email,
          phone: phone || undefined,
          classIds: Array.from(classIds),
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teachers'] })
      setFullName('')
      setEmail('')
      setPhone('')
      setClassIds(new Set())
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/v1/teachers/${id}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teachers'] }),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    create.mutate()
  }

  const toggleClass = (id: string) => {
    setClassIds((curr) => {
      const next = new Set(curr)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const columns: Column<TeacherRow>[] = [
    {
      key: 'name',
      header: 'Teacher',
      render: (t) => (
        <div>
          <div className="font-medium text-fg">{t.fullName ?? t.email}</div>
          <div className="text-xs text-fg-subtle">{t.email}</div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (t) => (
        <span className="text-fg-muted">{t.phone ?? '—'}</span>
      ),
    },
    {
      key: 'classes',
      header: 'Classes',
      render: (t) =>
        t.classes.length === 0 ? (
          <span className="text-fg-subtle italic">Unassigned</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {t.classes.map((c) => (
              <span
                key={c.id}
                className="rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs text-fg-muted"
              >
                {c.name}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => (
        <StatusPill tone={t.isActive ? 'active' : 'cancelled'}>
          {t.isActive ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (t) =>
        t.isActive ? (
          <button
            onClick={() => {
              if (
                confirm(`Deactivate ${t.fullName ?? t.email}? They will lose access immediately.`)
              ) {
                deactivate.mutate(t.userId)
              }
            }}
            className="text-xs text-fg-muted hover:text-danger"
          >
            Deactivate
          </button>
        ) : null,
    },
  ]

  const activeClasses = classes.data?.filter((c) => c.isActive) ?? []

  return (
    <div className="space-y-8">
      <PageHeader
        title="Teachers"
        description="Add teachers and assign them to classes. They can mark attendance and enter test scores for their assigned classes only."
      />

      <Section title="Add a teacher">
        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Full name" required>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </Field>
              <Field label="Email" required>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
              <PhoneField
                label="Phone"
                optional
                value={phone}
                onChange={setPhone}
              />
            </form>

            {activeClasses.length > 0 ? (
              <div className="mt-5">
                <p className="text-sm font-medium text-fg mb-2">Assigned classes</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 border-t border-border pt-3">
                  {activeClasses.map((c) => (
                    <Checkbox
                      key={c.id}
                      label={c.name}
                      checked={classIds.has(c.id)}
                      onChange={() => toggleClass(c.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-5 text-sm text-fg-subtle italic">
                No active classes yet. Create one in Classes first, then assign it
                from this teacher's row.
              </p>
            )}
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            {error ? <p className="text-sm text-danger">{error}</p> : <span />}
            <Button
              onClick={() => create.mutate()}
              disabled={!fullName || !email || create.isPending}
              loading={create.isPending}
            >
              Add teacher
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All teachers" description={`${teachers.data?.length ?? 0} total`}>
        <DataTable
          columns={columns}
          rows={teachers.data ?? []}
          rowKey={(t) => t.userId}
          loading={teachers.isLoading}
          empty="No teachers yet. Add one above."
        />
      </Section>

      <Card>
        <CardHeader
          title="What teachers can do"
          description="Permissions are scoped to their assigned classes — they never see other classes' students or marks."
        />
        <CardBody>
          <ul className="text-sm text-fg-muted space-y-1.5">
            <li>• Mark daily attendance for assigned classes</li>
            <li>• Create tests and enter bulk scores</li>
            <li>• Add private preparation notes (parents never see these)</li>
            <li>• See exam schedules for their classes (Phase 6)</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  )
}
